import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { OrganizationActions } from './organization.actions';
import { OrganizationModels } from '@share-models';
import { AuthActions } from '../auth/auth.actions';

export type OrganizationSwitchStatus =
  | 'idle'
  | 'switchingToken'
  | 'loadingOrganization'
  | 'loadingChat'
  | 'organizationLoadError';

export declare interface OrganizationState {
  activeOrganization: OrganizationModels.OrganizationDocument | null;
  isDefaultOrganization: boolean;
  userOrganizations: OrganizationModels.UserOrganizationDropDown[];
  loaded: boolean;
  switchStatus: OrganizationSwitchStatus;
  switchTargetOrganizationId: string | null;
  switchError: string | null;
}

const initialState: OrganizationState = {
  activeOrganization: null,
  isDefaultOrganization: false,
  userOrganizations: [],
  loaded: false,
  switchStatus: 'idle',
  switchTargetOrganizationId: null,
  switchError: null,
};

export const organizationStateReducer = createReducer(
  initialState,
  on(OrganizationActions.resetToInitialState, () => initialState),
  on(AuthActions.switchOrganizationRequested, (state, { organizationId }) => ({
    ...state,
    switchStatus: 'switchingToken' as const,
    switchTargetOrganizationId: organizationId,
    switchError: null,
  })),
  on(AuthActions.organizationSwitchFailed, (state, { organizationId }) =>
    state.switchTargetOrganizationId === organizationId
      ? {
          ...state,
          switchStatus: 'idle' as const,
          switchTargetOrganizationId: null,
        }
      : state,
  ),
  on(
    AuthActions.organizationSwitched,
    AuthActions.retryOrganizationDataLoad,
    (state, { organizationId }) => ({
      ...state,
      activeOrganization: null,
      isDefaultOrganization: false,
      loaded: false,
      switchStatus: 'loadingOrganization' as const,
      switchTargetOrganizationId: organizationId,
      switchError: null,
    }),
  ),
  on(
    AuthActions.organizationDataLoadFailed,
    (state, { organizationId, message }) =>
      state.switchTargetOrganizationId === organizationId
        ? {
            ...state,
            activeOrganization: null,
            loaded: true,
            switchStatus: 'organizationLoadError' as const,
            switchError: message,
          }
        : state,
  ),
  on(
    OrganizationActions.completeOrganizationSwitch,
    (state, { organizationId }) =>
      state.switchTargetOrganizationId === organizationId &&
      state.switchStatus === 'loadingChat'
        ? {
            ...state,
            switchStatus: 'idle' as const,
            switchTargetOrganizationId: null,
            switchError: null,
          }
        : state,
  ),
  on(
    OrganizationActions.setActiveOrganization,
    (state, { activeOrganization }) => ({
      ...state,
      activeOrganization,
      loaded: true,
    }),
  ),
  on(OrganizationActions.getActiveOrganizationFail, (state) => ({
    ...state,
    loaded: true,
  })),
  on(
    OrganizationActions.setSwitchedActiveOrganization,
    (state, { organizationId, activeOrganization }) =>
      state.switchTargetOrganizationId === organizationId &&
      activeOrganization._id === organizationId
        ? {
            ...state,
            activeOrganization,
            loaded: true,
            switchStatus: 'loadingChat' as const,
            switchError: null,
          }
        : state,
  ),
  on(
    OrganizationActions.getMyOrganizationsSuccess,
    (state, { userOrganizations }) => ({
      ...state,
      userOrganizations,
    }),
  ),
);

export const organizationFeature = createFeature({
  name: 'organization',
  reducer: organizationStateReducer,
  extraSelectors: ({ selectSwitchStatus }) => ({
    selectSwitching: createSelector(
      selectSwitchStatus,
      (status) =>
        status === 'switchingToken' ||
        status === 'loadingOrganization' ||
        status === 'loadingChat',
    ),
  }),
});
